import { Colors, Icon, Text } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import PillarStatusMention from './PillarStatusMention';

import type { PillarSummary } from '@/types/objectives';
import type { ReactNode } from 'react';

import { ON_COLOR_PILL, PILLAR_TILE } from '@/components/objectives/objectiveStyles';
import PillarIcon from '@/components/objectives/PillarIcon';
import {
    derivePillarTileGradient,
    needsReadabilityOverlay,
} from '@/components/objectives/pillarSurface';
import { TileLink } from '@/components/TileLink';
import { useNavigate } from '@/hooks/utils/useNavigate';
import { pillarPath, stepBackState } from '@/navigation/objectivesRoutes';
import i18n from '@/plugins/i18n';
import { resolveCompanyValueIconBackground } from '@/utils/companyValueIconBackground';
import { getLocalizedText } from '@/utils/localization';
import { formatPercent } from '@/utils/numberFormat';
import { clampPercent } from '@/utils/objectiveProgress';

const ICON_SIZE = 40;

interface PillarStatRowProps {
    icon: 'users-people' | 'business-trend-up';
    label: string;
    value: string | number;
}

/** The prototype's `StatRow`: icon + label on the left, the raw value on the right. */
function PillarStatRow({ icon, label, value }: PillarStatRowProps) {
    return (
        <div className={'flex items-center justify-between gap-3'}>
            <div className={'flex min-w-0 items-center gap-1.5'}>
                <Icon
                    name={icon}
                    size={'lg'}
                    colorClassName={'text-white'}
                />

                <span className={'text-sm font-medium text-white'}>{label}</span>
            </div>

            <span className={'flex-shrink-0 text-[20px] font-medium text-white'}>{value}</span>
        </div>
    );
}

interface PillarSummaryCardProps {
    pillar: PillarSummary;
    /**
     * The query string of the screen the tile is read on, carried whole to the pillar page: its
     * period, filters and pillar scope are the ones the tile was counted in.
     */
    lensSearch?: string;
    /** A mark on the tile, such as the employee portal's "you contribute here": it takes its own line
     *  under the icon, where the full width of the tile is free, rather than the narrow space beside it. */
    badge?: ReactNode;
    /**
     * Whether the tile leads to the pillar page. When it does not, the tile is plainly not a link:
     * no anchor, no click, no pointer, no hover.
     */
    linksToPillarPage?: boolean;
    /** Replaces the default "Progression" label when the screen has to say whose progression it is. */
    progressionLabel?: string;
}

export default function PillarSummaryCard({
    pillar,
    lensSearch = '',
    badge,
    linksToPillarPage = true,
    progressionLabel,
}: PillarSummaryCardProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const displayName = getLocalizedText(pillar.name);
    const displayDescription = getLocalizedText(pillar.description);
    const cardBackground = derivePillarTileGradient(
        resolveCompanyValueIconBackground(pillar.icon, pillar.iconBackground),
    );
    const clampedProgression = clampPercent(pillar.progression);
    // The derived gradient is deep and saturated for most colours, so most tiles need no scrim;
    // a very pale base colour still gets one, so white text stays readable on it.
    const overlayClassName = needsReadabilityOverlay(cardBackground)
        ? ' before:absolute before:inset-0 before:rounded-[inherit] before:bg-black/25'
        : '';

    const pillarPagePath = pillarPath({ pillarId: pillar.id, lensSearch });
    const affordanceClassName = linksToPillarPage
        ? ' cursor-pointer transition-transform hover:scale-105'
        : '';

    // When the card leads to the pillar, a click anywhere on it does; the keyboard and assistive
    // technology reach it through the title, the card's one link. When it does not, the card offers
    // no click and no link at all, so nothing about it reads as reachable.
    return (
        <div
            onClick={
                linksToPillarPage
                    ? () => navigate({ path: pillarPagePath, state: stepBackState() })
                    : undefined
            }
            style={{ background: cardBackground }}
            className={`${PILLAR_TILE.surface} text-left backdrop-blur-[2px]${affordanceClassName}${overlayClassName}`}
            data-testid={`pillar-summary-card-${pillar.id}`}
        >
            <div className={PILLAR_TILE.header}>
                <div
                    className={`${PILLAR_TILE.iconBox} flex items-center justify-center overflow-hidden bg-white/45`}
                >
                    <PillarIcon
                        icon={pillar.icon}
                        size={ICON_SIZE}
                    />
                </div>

                <div className={'flex flex-wrap items-center justify-end gap-2'}>
                    <PillarStatusMention
                        status={pillar.status}
                        tone={'onColor'}
                        dataTestId={`pillar-summary-card-${pillar.id}-inactive`}
                    />

                    <span
                        className={ON_COLOR_PILL}
                        data-testid={`pillar-summary-card-${pillar.id}-count`}
                    >
                        {t('objectives.pillars.objectivesCount', { count: pillar.objectivesCount })}
                    </span>
                </div>
            </div>

            {badge && <div className={'mb-2 flex flex-shrink-0'}>{badge}</div>}

            <div className={'relative min-h-0 flex-1'}>
                <h3 className={'mb-1.5 text-[20px] leading-[25px] font-medium text-white'}>
                    {linksToPillarPage ? (
                        <TileLink
                            to={pillarPagePath}
                            state={stepBackState()}
                            dataTestId={`pillar-summary-card-${pillar.id}-title`}
                        >
                            {displayName}
                        </TileLink>
                    ) : (
                        displayName
                    )}
                </h3>

                {/* The row already grows to its longest description; past four lines the tiles
                    would be taller than the screen, so the text ends in an ellipsis and stays
                    readable in full through its native tooltip. */}
                {displayDescription && (
                    <div
                        title={displayDescription}
                        data-testid={`pillar-summary-card-${pillar.id}-description`}
                    >
                        <Text
                            color={Colors.WHITE}
                            truncate={4}
                            className={'leading-normal'}
                        >
                            {displayDescription}
                        </Text>
                    </div>
                )}
            </div>

            <div className={PILLAR_TILE.statsBlock}>
                <PillarStatRow
                    icon={'users-people'}
                    label={t('objectives.pillars.participantsLabel', {
                        count: pillar.participantsCount,
                    })}
                    value={pillar.participantsCount}
                />

                <PillarStatRow
                    icon={'business-trend-up'}
                    label={progressionLabel ?? t('objectives.pillars.progression')}
                    value={formatPercent(clampedProgression, i18n.language)}
                />
            </div>

            <div className={PILLAR_TILE.progressAnchor}>
                <div
                    className={`${PILLAR_TILE.progressTrack} bg-white/45 shadow-sm`}
                    role={'progressbar'}
                    aria-label={t('objectives.pillars.progressAria', {
                        name: displayName,
                        value: pillar.progression,
                    })}
                    aria-valuenow={clampedProgression}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    data-testid={`pillar-summary-card-${pillar.id}-progress`}
                >
                    <div
                        className={
                            'h-full rounded-full bg-white shadow-sm transition-all duration-500'
                        }
                        style={{ width: `${clampedProgression}%` }}
                    />
                </div>
            </div>
        </div>
    );
}
